import { useShowNotification } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useCertificateDownload } from '@inflearn/modals';
import { useDisclosure } from '@mantine/hooks';
import { isNil } from 'lodash-es';

import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import type { CourseType } from '../../resources/@types/courseSchema';
import useClientAccountProfileQuery from '../queries/useClientAccountProfileQuery';

/**
 * 수료증 다운, 모달 열기
 * 실명 없을 때 실명인증 모달을 열어준다.
 */
export const useCertificate = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const { showNotification } = useShowNotification();

  const { data: accountProfile } = useClientAccountProfileQuery();
  const isVerifiedRealName = !!accountProfile?.realname;

  const { onClickDownloadCertificate } = useCertificateDownload(lang);
  const [isRealNameModalOpened, realNameModalHandler] = useDisclosure();
  const [isCertificatedModalOpened, certificatedModalHandler] = useDisclosure();

  const [isPdfDownloadLoading, setIsPdfDownloadLoading] = useState(false);

  const onClickPdfDownload = ({
    userId,
    courseId,
    voucherId,
    courseType,
  }: {
    userId: number;
    courseId: number;
    voucherId: number;
    courseType: CourseType;
  }) => {
    setIsPdfDownloadLoading(true);
    void onClickDownloadCertificate({
      courseType,
      userId,
      courseId,
      voucherId,
      downloadFileName: 'certificate',
      options: {
        onSuccess: () => setIsPdfDownloadLoading(false),
        onError: () => setIsPdfDownloadLoading(false),
      },
    });
  };

  /**
   * 실명이 없으면 실명인증 모달을 띄워준다.
   * 실명이 있으면 수료증 미리보기 모달을 띄워준다.
   */
  const onClickCertificateModalOpen = ({
    userId,
    courseId,
    voucherId,
  }: {
    userId: number;
    courseId: number;
    voucherId: number;
  }) => {
    if (isNil(userId) || isNil(courseId) || isNil(voucherId)) {
      return showNotification({
        type: 'error',
        title: t('시리얼 코드가 유효하지 않습니다.'),
        message: t('에러가 반복되면 고객센터로 문의해주세요.'),
      });
    }
    certificatedModalHandler.open();
  };

  function realNameGuard(fn: (...args: any[]) => void) {
    return (...args: unknown[]) => {
      if (!isVerifiedRealName) {
        realNameModalHandler.open();

        return;
      }

      return fn(...args);
    };
  }

  return {
    isRealNameModalOpened,
    isPdfDownloadLoading,
    closeRealNameModal: realNameModalHandler.close,
    closeCertificate: certificatedModalHandler.close,
    isCertificatedModalOpened,
    onClickPdfDownload: realNameGuard(onClickPdfDownload),
    onClickCertificateModalOpen: realNameGuard(onClickCertificateModalOpen),
  };
};
